import { UTCDate } from '@date-fns/utc';
import { getMonth, getYear } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, DayPickerProps, NextMonthButtonProps, PreviousMonthButtonProps, Weekdays } from 'react-day-picker';
import { enIE } from 'react-day-picker/locale';
// https://daypicker.dev/docs/styling#default-style
import 'react-day-picker/style.css';
import { Button } from '../button/button';

export const Calendar = ({ className, classNames, showOutsideDays = true, ...props }: DayPickerProps) => {
  return (
    <DayPicker
      navLayout="around"
      showOutsideDays={showOutsideDays}
      numberOfMonths={2}
      className={className}
      // Always displays current month as the last available month
      // https://daypicker.dev/docs/navigation#start-and-end-dates
      endMonth={new UTCDate(getYear(new UTCDate()), getMonth(new UTCDate()))}
      locale={enIE}
      classNames={{
        range_start: 'rounded-l-full bg-black text-seapoint-yellow',
        range_end: 'rounded-r-full bg-black text-seapoint-yellow',
        range_middle: 'bg-seapoint-gray-medium',
        selected: 'text-sm',
        month: 'mx-auto w-full',
        months: 'flex gap-3',
        month_grid: 'mt-3',
        day: 'h-9 w-9 font-medium text-center',
        day_button: 'h-full w-full',
        today: 'text-black font-extrabold',
        caption_label: 'text-sm font-medium text-black spacing-5 tracking-tight my-auto',
        outside: 'text-seapoint-gray-text',
        ...classNames,
      }}
      components={{
        Weekdays: CustomWeekdays,
        PreviousMonthButton(props) {
          return <CustomNavButton {...props} direction="previous" />;
        },
        NextMonthButton(props) {
          return <CustomNavButton {...props} direction="next" />;
        },
      }}
      {...props}
    />
  );
};

const days = [
  { label: 'M', value: 'Monday' },
  { label: 'T', value: 'Tuesday' },
  { label: 'W', value: 'Wednesday' },
  { label: 'T', value: 'Thursday' },
  { label: 'F', value: 'Friday' },
  { label: 'S', value: 'Saturday' },
  { label: 'S', value: 'Sunday' },
];

const CustomWeekdays = () => {
  return (
    <Weekdays>
      {days.map((day) => (
        <th key={day.value} className="text-xs font-normal text-seapoint-gray-text">
          {day.label}
        </th>
      ))}
    </Weekdays>
  );
};

type CustomNavButtonProps = (PreviousMonthButtonProps | NextMonthButtonProps) & {
  direction: 'previous' | 'next';
};

const CustomNavButton = (props: CustomNavButtonProps) => {
  const { direction, ...rest } = props;

  return (
    <Button variant="ghost" size="icon" {...rest}>
      <div className="h-7 w-7 rounded-full bg-seapoint-gray-medium p-1.5 text-black">
        {direction === 'previous' ? (
          <ChevronLeft className="h-full w-full" />
        ) : (
          <ChevronRight className="h-full w-full" />
        )}
      </div>
    </Button>
  );
};
